import { useState } from "react";
import {
  Box,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { motion, Variants } from "framer-motion";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { SAMPLE_QUESTIONS, DifficultyLevel } from "../data/QuestionDifficultySamples";
import { display } from "html2canvas/dist/types/css/property-descriptors/display";
import PageMeta from '../components/PageMeta';

// ---------------------------------------------------------------------------
// Animation variants — mirrors QuestionBreakdownPage
// ---------------------------------------------------------------------------

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.4,
      ease: "easeOut" as const,
    },
  }),
};

// ---------------------------------------------------------------------------
// Difficulty colour config
// ---------------------------------------------------------------------------

type DifficultyKey = DifficultyLevel["key"];

const DIFFICULTY_CHIP_SX: Record<DifficultyKey, object> = {
  easy:       { backgroundColor: "#388e3c", color: "#F5F5F5" },
  medium:     { backgroundColor: "#689f38", color: "#F5F5F5" },
  hard:       { backgroundColor: "#f57c00", color: "#F5F5F5" },
  veryHard:   { backgroundColor: "#e64a19", color: "#F5F5F5" },
  impossible: { backgroundColor: "#d32f2f", color: "#F5F5F5" },
};

// Header band background and text colours for each difficulty.
// We keep these as explicit values so they're readable in both themes via
// MUI's `sx` prop — using a theme-aware approach (light: tint, dark: shade).
// TODO: DRY - Consolidate colors with QuestionCard & Quiz page

// TODO - import colors per difficulty
const DIFFICULTY_BAND_SX: Record<DifficultyKey, object> = {
  easy: {
    backgroundColor: (theme: any) =>
      theme.palette.mode === "dark" ? "#1B4D1E" : "#E8F5E9",
  },
  medium: {
    backgroundColor: (theme: any) =>
      theme.palette.mode === "dark" ? "#33501A" : "#F1F8E9",
  },
  hard: {
    backgroundColor: (theme: any) =>
      theme.palette.mode === "dark" ? "#7A3D00" : "#FFF3E0",
  },
  veryHard: {
    backgroundColor: (theme: any) =>
      theme.palette.mode === "dark" ? "#7A240D" : "#FBE9E7",
  },
  impossible: {
    backgroundColor: (theme: any) =>
      theme.palette.mode === "dark" ? "#6B1515" : "#FFEBEE",
  },
};

const WHITESMOKE = "#F5F5F5";

const DIFFICULTY_FLAVOR_COLOR: Record<DifficultyKey, object> = {
  easy:       { color: WHITESMOKE },
  medium:     { color: WHITESMOKE },
  hard:       { color: WHITESMOKE },
  veryHard:   { color: WHITESMOKE },
  impossible: { color: WHITESMOKE },
};

const DIFFICULTY_ANSWER_SX: Record<DifficultyKey, object> = {
  easy: {
    borderLeft: "3px solid",
    borderColor: (theme: any) =>
      theme.palette.mode === "dark" ? "#388e3c" : "#66BB6A",
    backgroundColor: (theme: any) =>
      theme.palette.mode === "dark" ? "#0D2E0F" : "#F1F8F1",
  },
  medium: {
    borderLeft: "3px solid",
    borderColor: (theme: any) =>
      theme.palette.mode === "dark" ? "#689f38" : "#8BC34A",
    backgroundColor: (theme: any) =>
      theme.palette.mode === "dark" ? "#1C2E0A" : "#F5F9EE",
  },
  hard: {
    borderLeft: "3px solid",
    borderColor: (theme: any) =>
      theme.palette.mode === "dark" ? "#f57c00" : "#FFA726",
    backgroundColor: (theme: any) =>
      theme.palette.mode === "dark" ? "#3D1F00" : "#FFF8F0",
  },
  veryHard: {
    borderLeft: "3px solid",
    borderColor: (theme: any) =>
      theme.palette.mode === "dark" ? "#e64a19" : "#FF7043",
    backgroundColor: (theme: any) =>
      theme.palette.mode === "dark" ? "#3D1006" : "#FFF4F1",
  },
  impossible: {
    borderLeft: "3px solid",
    borderColor: (theme: any) =>
      theme.palette.mode === "dark" ? "#d32f2f" : "#EF5350",
    backgroundColor: (theme: any) =>
      theme.palette.mode === "dark" ? "#330A0A" : "#FFF5F5",
  },
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface QuestionCardProps {
  question: string;
  answer: string;
  difficultyKey: DifficultyKey;
  isRevealed: boolean;
  onToggle: () => void;
}

function QuestionCard({
  question,
  answer,
  difficultyKey,
  isRevealed,
  onToggle,
}: QuestionCardProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        transition: "box-shadow 0.2s ease",
        "&:hover": { boxShadow: 2 },
      }}
    >
      {/* Question row */}
      <Box
        onClick={onToggle}
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          cursor: "pointer",
          userSelect: "none",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <Typography
          variant="body1"
          sx={{ flex: 1, lineHeight: 1.65, fontWeight: 400 }}
        >
          {question}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
            pt: 0.2,
          }}
        >
          <Typography
            variant="caption"
            fontWeight={500}
            color="text.secondary"
            sx={{ whiteSpace: "nowrap", fontSize: 14 }}
          >
            {isRevealed ? "Hide answer" : "Reveal answer"}
          </Typography>
          {isRevealed ? (
            <ExpandLessIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          )}
        </Box>
      </Box>

      {/* Answer row */}
      {isRevealed && (
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderRadius: 0,
            ...DIFFICULTY_ANSWER_SX[difficultyKey],
          }}
        >
          <Typography
            variant="overline"
            sx={{
              display: "block",
              fontSize: "0.65rem",
              letterSpacing: "0.08em",
              color: "text.secondary",
              mb: 0.5,
              lineHeight: 1,
            }}
          >
            Answer
          </Typography>
          <Typography variant="body2" fontWeight={500} lineHeight={1.6}>
            {answer}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Difficulty section
// ---------------------------------------------------------------------------

interface DifficultySection {
  level: DifficultyLevel;
  animationIndex: number;
  revealedIds: Set<string>;
  onToggle: (id: string) => void;
}

function DifficultySectionPanel({
  level,
  animationIndex,
  revealedIds,
  onToggle,
}: DifficultySection) {

  return (
    <motion.div
      custom={animationIndex}
      initial="hidden"
      animate="visible"
      variants={fadeUp}
    >
      <Paper
        variant="outlined"
        sx={{ borderRadius: 3, overflow: "hidden", mb: 3 }}
      >
        {/* Coloured header band */}
        <Box
          sx={{
            px: { xs: 2.5, md: 3 },
            py: 2,
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 2 },
            ...DIFFICULTY_BAND_SX[level.key],
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip label={level.label} sx={DIFFICULTY_CHIP_SX[level.key]} size="small" />
            <Typography
              variant="body2"
              fontWeight={500}
              sx={DIFFICULTY_FLAVOR_COLOR[level.key]}
            >
              3 Sample Questions
            </Typography>
          </Stack>

          <Typography
            variant="body2"
            sx={{
              maxWidth: { sm: 420 },
              lineHeight: 1.5,
              ...DIFFICULTY_FLAVOR_COLOR[level.key],
            }}
          >
            {level.flavor}
          </Typography>
        </Box>

        {/* Question cards */}
        <Stack spacing={1.5} sx={{ p: { xs: 2, md: 2.5 } }}>
          {level.questions.map((item: any) => (
            <QuestionCard
              key={item.id}
              question={item.question}
              answer={item.answer}
              difficultyKey={level.key}
              isRevealed={revealedIds.has(item.id)}
              onToggle={() => onToggle(item.id)}
            />
          ))}
        </Stack>
      </Paper>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function QuestionDifficultyPage() {
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());

  const handleToggle = (id: string) => {
    setRevealedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <>
      <PageMeta
        title="Sample Questions by Difficulty | The Quizzler"
        description="Not sure which difficulty to start on? Try sample Harry Potter trivia questions from all five levels — Easy to Impossible — and find your level."
        canonical="https://thequizzler.app/question-difficulty"
      />
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h3"
            fontWeight={700}
            gutterBottom
            sx={{
              letterSpacing: "-0.5px",
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Which Difficulty Levels Are Right For You?
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 700 }}
          >
            Not sure which difficulty to start on? Here's a taste of what each
            level feels like. Click{" "}
            <Typography component="span" variant="body1" fontWeight={600}>
              Reveal answer
            </Typography>{" "}
            on any card to check how you did — no pressure.
          </Typography>

          <Divider sx={{ mb: 4 }} />
        </motion.div>

        {/* Difficulty intro callout */}
        <motion.div
          custom={0}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 3,
              mb: 4,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            <LightbulbOutlinedIcon
              color="primary"
              sx={{ mt: 0.2, flexShrink: 0 }}
            />
            <Typography component="span" variant="body2" color="text.secondary" lineHeight={1.7} sx={{display: "block", fontSize: 15}}>
              The Quizzler has five difficulty tiers — from{" "}
              <Chip label="Easy" color="success" size="small" sx={DIFFICULTY_CHIP_SX["easy"]} />{" "}
              warm-ups all the way to{" "}
              <Chip
                label="Impossible"
                color="error"
                size="small"
                sx={DIFFICULTY_CHIP_SX["impossible"]}
              />{" "}
              questions that will stump even the most devoted fans. The settings allow you to choose any or all of these options, so there's always something to
              interest you no matter how well you know the wizarding world.
            </Typography>
          </Paper>
        </motion.div>

        {/* Difficulty sections */}
        {SAMPLE_QUESTIONS.map((level: any, i: any) => (
          <DifficultySectionPanel
            key={level.key}
            level={level}
            animationIndex={i + 1}
            revealedIds={revealedIds}
            onToggle={handleToggle}
          />
        ))}
      </Container>
    </>
  );
}